import React, { useEffect, useState } from 'react';
import useAxios from '../utils/useAxios';
import Swal from 'sweetalert2';
import { FaEye, FaInfoCircle } from 'react-icons/fa';


const styles = {
  container: {
    padding: '20px',
    minHeight: 'calc(100vh - 100px)',
    backgroundColor: '#f4f6f9',
    fontFamily: 'Segoe UI, sans-serif',
  },
  header: {
    color: '#1B019B',
    fontWeight: '600',
    fontSize: '1.5rem',
  },
  hr: {
    width: '80px',
    border: '2px solid #1B019B',
    opacity: '1',
    margin: '10px auto',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  tableHeader: {
    backgroundColor: '#1B019B',
    color: '#fff',
    whiteSpace: 'nowrap',
  },
  submitButton: {
    backgroundColor: '#1B019B',
    color: 'white',
  },
  paginationButton: {
    backgroundColor: '#fff',
    border: '1px solid #1B019B',
    color: '#1B019B',
    margin: '0 2px',
    borderRadius: '5px',
    padding: '5px 10px',
    cursor: 'pointer',
  },
  paginationActive: {
    backgroundColor: '#1B019B',
    color: '#fff',
  },
  paginationDisabled: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
  detailButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#1B019B',
    cursor: 'pointer',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  modalBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1050,
    padding: '10px',
  },
  modalContent: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '12px',
    maxWidth: '500px',
    width: '100%',
    maxHeight: '80vh',
    overflowY: 'auto',
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '10px',
  },
  detailItem: {
    marginBottom: '10px',
    fontSize: '14px',
  },
  justificationText: {
    backgroundColor: '#f8f9fa',
    padding: '10px',
    borderRadius: '5px',
    marginTop: '5px',
    fontSize: '14px',
  },
  fileLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    color: '#1B019B',
    textDecoration: 'none',
    marginTop: '5px',
  },
};

const MesRetardsPage = () => {
  const axiosInstance = useAxios();
  const [retards, setRetards] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedRetard, setSelectedRetard] = useState(null);

  const [date, setDate] = useState('');
  const [heureEstimee, setHeureEstimee] = useState('');
  const [motif, setMotif] = useState('');
  const [justificatif, setJustificatif] = useState(null);

  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ totalPages: 1, count: 0 });
  const [page, setPage] = useState(1);

  const fetchRetards = async (pageNum = 1) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/pointage/mes-retards/', { params: { page: pageNum } });
      setRetards(res.data.results);
      setPagination({
        totalPages: Math.ceil(res.data.count / 10),
        count: res.data.count,
      });
      setPage(pageNum);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: "Impossible de charger vos retards.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRetards();
  }, []);

  const handlePageChange = (pageNum) => {
    if (pageNum < 1 || pageNum > pagination.totalPages || loading) return;
    fetchRetards(pageNum);
  };

  const renderBadge = (statut) => {
    switch (statut) {
      case 'en_attente':
        return <span className="badge bg-warning text-dark">En attente</span>;
      case 'approuvée':
        return <span className="badge bg-success">Acceptée</span>;
      case 'refusée':
        return <span className="badge bg-danger">Refusée</span>;
      default:
        return <span className="badge bg-secondary">Inconnu</span>;
    }
  };

  const openDetailModal = (retard) => {
    setSelectedRetard(retard);
    setDetailModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('date', date);
    formData.append('heure_estimee_arrivee', heureEstimee);
    formData.append('motif', motif);
    if (justificatif) formData.append('justificatif', justificatif);

    try {
      setLoading(true);
      await axiosInstance.post('/pointage/retards-employe/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setModalOpen(false);
      setDate('');
      setHeureEstimee('');
      setMotif('');
      setJustificatif(null);

      Swal.fire({
        icon: 'success',
        title: 'Envoyé !',
        text: 'Votre retard a été signalé avec succès.',
        timer: 2000,
        showConfirmButton: false,
      });
      fetchRetards(page);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: error.response?.data?.detail || "Une erreur est survenue lors de l'envoi.",

      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={styles.container}>
        <div className="text-center mb-4">
          <h3 style={styles.header}>Mes demandes de retard</h3>
          <hr style={styles.hr} />
        </div>

        <div className="d-flex justify-content-end mb-3">
          <button
            className="btn"
            style={styles.submitButton}
            onClick={() => setModalOpen(true)}
            disabled={loading}
          >
            Signaler un retard
          </button>
        </div>

        <div className="card shadow-sm">
          <div className="card-body p-0" style={styles.tableContainer}>
            {loading ? (
              <p className="text-center my-4">Chargement des retards...</p>
            ) : (
              <table className="table table-hover text-center align-middle m-0">
                <thead style={styles.tableHeader}>
                  <tr>
                    <th>Date</th>
                    <th>Heure estimée</th>
                    <th>Justificatif</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {retards.length > 0 ? (
                    retards.map((r) => (
                      <tr key={r.id}>
                        <td>{r.date}</td>
                        <td>{r.heure_estimee_arrivee}</td>
                        <td>
                          <button
                            onClick={() => openDetailModal(r)}
                            style={styles.detailButton}
                            title="Voir les détails"
                          >
                            <FaInfoCircle /> Détail
                          </button>
                        </td>
                        <td>{renderBadge(r.statut)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-muted">
                        Aucun retard signalé.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          {pagination.totalPages > 1 && (
            <div className="card-footer d-flex justify-content-center mt-3 bg-transparent border-top-0">
              <nav>
                <ul className="pagination mb-0">
                  <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(1)}
                      disabled={loading || page === 1}
                      style={page === 1 ? styles.paginationDisabled : styles.paginationButton}
                    >
                      Première
                    </button>
                  </li>
                  <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={loading || page === 1}
                      style={page === 1 ? styles.paginationDisabled : styles.paginationButton}
                    >
                      &laquo;
                    </button>
                  </li>

                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }

                    return (
                      <li key={pageNum} className={`page-item ${page === pageNum ? 'active' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(pageNum)}
                          disabled={loading}
                          style={page === pageNum
                            ? { ...styles.paginationButton, ...styles.paginationActive }
                            : styles.paginationButton}
                        >
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}

                  <li className={`page-item ${page === pagination.totalPages ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={loading || page === pagination.totalPages}
                      style={page === pagination.totalPages ? styles.paginationDisabled : styles.paginationButton}
                    >
                      &raquo;
                    </button>
                  </li>
                  <li className={`page-item ${page === pagination.totalPages ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(pagination.totalPages)}
                      disabled={loading || page === pagination.totalPages}
                      style={page === pagination.totalPages ? styles.paginationDisabled : styles.paginationButton}
                    >
                      Dernière
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>

        {/* Modal Détail */}
        {detailModalOpen && selectedRetard && (
          <div style={styles.modalBackdrop} onClick={() => setDetailModalOpen(false)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <h5 className="mb-3 text-center" style={{ color: '#1B019B' }}>
                Détail du retard
              </h5>

              <div style={styles.detailGrid}>
                <div style={styles.detailItem}>
                  <strong>Date:</strong> {selectedRetard.date}
                </div>
                <div style={styles.detailItem}>
                  <strong>Heure estimée d'arrivée:</strong> {selectedRetard.heure_estimee_arrivee}
                </div>
                <div style={styles.detailItem}>
                  <strong>Statut:</strong> {renderBadge(selectedRetard.statut)}
                </div>
                <div style={styles.detailItem}>
                  <strong>Motif:</strong>
                  <div style={styles.justificationText}>{selectedRetard.motif}</div>
                </div>
                {selectedRetard.justificatif && (
                  <div style={styles.detailItem}>
                    <strong>Fichier joint:</strong>
                    <a
                      href={selectedRetard.justificatif}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.fileLink}
                    >
                      <FaEye /> Voir le fichier
                    </a>
                  </div>
                )}
                {selectedRetard.statut === 'refusée' && selectedRetard.motif_refus && (
                  <div style={styles.detailItem}>
                    <strong>Motif de refus:</strong>
                    <div style={{ ...styles.justificationText, color: '#dc3545' }}>
                      {selectedRetard.motif_refus}
                    </div>
                  </div>
                )}
              </div>

              <div className="text-end mt-3">
                <button className="btn btn-secondary" onClick={() => setDetailModalOpen(false)}>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Nouveau Retard */}
        {modalOpen && (
          <div style={styles.modalBackdrop} onClick={() => setModalOpen(false)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <h5 className="mb-4 text-center fw-bold" style={{ color: '#1B019B' }}>
                Signaler un nouveau retard
              </h5>

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label">Date du retard</label>
                  <input
                    type="date"
                    className="form-control"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Heure estimée d'arrivée</label>
                  <input
                    type="time"
                    className="form-control"
                    value={heureEstimee}
                    onChange={(e) => setHeureEstimee(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Motif du retard</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    value={motif}
                    onChange={(e) => setMotif(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Justificatif (facultatif)</label>
                  <input
                    type="file"
                    className="form-control"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setJustificatif(e.target.files[0])}
                    disabled={loading}
                  />
                </div>

                <div className="text-end">
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    onClick={() => setModalOpen(false)}
                    disabled={loading}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="btn"
                    style={styles.submitButton}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Envoi...
                      </>
                    ) : (
                      'Envoyer'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

     
    </div>
  );
};

export default MesRetardsPage;